import {
  TREE_SCALE_SIMULATION_PROFILES,
  type TreeScaleProfileId,
  type TreeScaleSimulationProfile,
} from './TreeScaleSimulationProfiles';

type SyntheticScaleMember = {
  id: number;
  parentA: number | null;
  parentB: number | null;
  generation: number;
  side: 'mother' | 'father' | 'both';
  normalizedName: string;
};

export type TreeScalePhaseDurations = {
  generationMs: number;
  relationshipLinkingMs: number;
  floaterScanMs: number;
  duplicateScanMs: number;
  projectionMs: number;
  totalMs: number;
};

export type TreeScaleSimulationResult = {
  profileId: TreeScaleProfileId;
  mode: 'SIMULATED';
  state: 'passed' | 'failed';
  targetUserCount: number;
  measuredSampleSize: number;
  projectionRequired: boolean;
  projectedMultiplier: number;
  phaseDurations: TreeScalePhaseDurations;
  measuredThroughputPerSecond: number;
  projectedTotalDurationMs: number;
  floaterCount: number;
  duplicateCount: number;
  checksum: number;
  cleanupCompleted: boolean;
  liveCertified: false;
  productionMutationOccurred: false;
  completedAt: string;
  error: string | null;
};

const results: TreeScaleSimulationResult[] = [];

function now(): number {
  if (
    typeof performance !== 'undefined' &&
    typeof performance.now === 'function'
  ) {
    return performance.now();
  }

  return Date.now();
}

function duration(start: number): number {
  return Math.max(
    0,
    Math.round((now() - start) * 100) / 100,
  );
}

function getProfile(
  profileId: TreeScaleProfileId,
): TreeScaleSimulationProfile {
  const profile =
    TREE_SCALE_SIMULATION_PROFILES.find(
      candidate => candidate.id === profileId,
    );

  if (!profile) {
    throw new Error(
      `Unknown Tree scale profile: ${profileId}`,
    );
  }

  return profile;
}

function generateMembers(
  count: number,
): SyntheticScaleMember[] {
  const members =
    new Array<SyntheticScaleMember>(count);

  for (let index = 0; index < count; index += 1) {
    const generation =
      index === 0
        ? 0
        : Math.floor(Math.log2(index + 1)) % 8;

    members[index] = {
      id: index,
      parentA:
        index > 0
          ? Math.floor((index - 1) / 2)
          : null,
      parentB:
        index > 2 && index % 5 === 0
          ? Math.max(
              0,
              Math.floor((index - 2) / 3),
            )
          : null,
      generation,
      side:
        index % 7 === 0
          ? 'both'
          : index % 2 === 0
            ? 'mother'
            : 'father',
      normalizedName:
        `member-${index % Math.max(1, count - 3)}`,
    };
  }

  return members;
}

function linkRelationships(
  members: SyntheticScaleMember[],
): number {
  let links = 0;

  for (const member of members) {
    if (member.parentA !== null) {
      links += 1;
    }

    if (member.parentB !== null) {
      links += 1;
    }
  }

  return links;
}

function scanFloaters(
  members: SyntheticScaleMember[],
): number {
  const referenced = new Set<number>();

  for (const member of members) {
    if (member.parentA !== null) {
      referenced.add(member.parentA);
    }

    if (member.parentB !== null) {
      referenced.add(member.parentB);
    }
  }

  let floaters = 0;

  for (const member of members) {
    if (
      member.id !== 0 &&
      member.parentA === null &&
      member.parentB === null &&
      !referenced.has(member.id)
    ) {
      floaters += 1;
    }
  }

  return floaters;
}

function scanDuplicates(
  members: SyntheticScaleMember[],
): number {
  const seen = new Set<string>();
  let duplicates = 0;

  for (const member of members) {
    const key =
      `${member.normalizedName}|${member.side}|${member.generation}`;

    if (seen.has(key)) {
      duplicates += 1;
    } else {
      seen.add(key);
    }
  }

  return duplicates;
}

function projectChecksum(
  members: SyntheticScaleMember[],
): number {
  let checksum = 0;

  for (const member of members) {
    checksum =
      (
        checksum +
        member.id * 31 +
        member.generation * 17 +
        (member.parentA ?? 0) * 7 +
        (member.parentB ?? 0) * 3
      ) %
      2_147_483_647;
  }

  return checksum;
}

export function listTreeScaleSimulationResults():
  TreeScaleSimulationResult[] {
  return [...results];
}

export function clearTreeScaleSimulationResults():
  void {
  results.length = 0;
}

export async function runTreeScaleSimulation(
  profileId: TreeScaleProfileId,
): Promise<TreeScaleSimulationResult> {
  const profile = getProfile(profileId);
  const totalStart = now();

  let members: SyntheticScaleMember[] = [];

  try {
    const generationStart = now();
    members = generateMembers(
      profile.measuredSampleSize,
    );
    const generationMs =
      duration(generationStart);

    const linkingStart = now();
    linkRelationships(members);
    const relationshipLinkingMs =
      duration(linkingStart);

    const floaterStart = now();
    const floaterCount =
      scanFloaters(members);
    const floaterScanMs =
      duration(floaterStart);

    const duplicateStart = now();
    const duplicateCount =
      scanDuplicates(members);
    const duplicateScanMs =
      duration(duplicateStart);

    const projectionStart = now();
    const checksum =
      projectChecksum(members);
    const projectionMs =
      duration(projectionStart);

    const totalMs =
      duration(totalStart);

    const projectedMultiplier =
      profile.targetUserCount /
      profile.measuredSampleSize;

    const measuredThroughputPerSecond =
      totalMs > 0
        ? Math.round(
            (
              profile.measuredSampleSize /
              totalMs
            ) *
              1000,
          )
        : profile.measuredSampleSize;

    const result: TreeScaleSimulationResult = {
      profileId,
      mode: 'SIMULATED',
      state: 'passed',
      targetUserCount:
        profile.targetUserCount,
      measuredSampleSize:
        profile.measuredSampleSize,
      projectionRequired:
        profile.projectionRequired,
      projectedMultiplier,
      phaseDurations: {
        generationMs,
        relationshipLinkingMs,
        floaterScanMs,
        duplicateScanMs,
        projectionMs,
        totalMs,
      },
      measuredThroughputPerSecond,
      projectedTotalDurationMs:
        Math.round(
          totalMs *
            projectedMultiplier *
            100,
        ) / 100,
      floaterCount,
      duplicateCount,
      checksum,
      cleanupCompleted: true,
      liveCertified: false,
      productionMutationOccurred: false,
      completedAt:
        new Date().toISOString(),
      error: null,
    };

    results.unshift(result);

    if (results.length > 20) {
      results.length = 20;
    }

    return result;
  } catch (error) {
    const result: TreeScaleSimulationResult = {
      profileId,
      mode: 'SIMULATED',
      state: 'failed',
      targetUserCount:
        profile.targetUserCount,
      measuredSampleSize:
        profile.measuredSampleSize,
      projectionRequired:
        profile.projectionRequired,
      projectedMultiplier:
        profile.targetUserCount /
        profile.measuredSampleSize,
      phaseDurations: {
        generationMs: 0,
        relationshipLinkingMs: 0,
        floaterScanMs: 0,
        duplicateScanMs: 0,
        projectionMs: 0,
        totalMs:
          duration(totalStart),
      },
      measuredThroughputPerSecond: 0,
      projectedTotalDurationMs: 0,
      floaterCount: 0,
      duplicateCount: 0,
      checksum: 0,
      cleanupCompleted: true,
      liveCertified: false,
      productionMutationOccurred: false,
      completedAt:
        new Date().toISOString(),
      error:
        error instanceof Error
          ? error.message
          : String(error),
    };

    results.unshift(result);
    return result;
  } finally {
    members = [];
  }
}
